import React, { useState } from 'react';
import { Truck, Mail, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './ui/LoadingSpinner';

export default function SignUpPage({ onSwitchToLogin }) {
  const { signUp, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(false);

  const ALLOWED_DOMAINS = ['locate2u.com', 'winning.com.au'];

  const validateEmailDomain = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain || !ALLOWED_DOMAINS.includes(domain)) {
      return `Sign up is restricted to ${ALLOWED_DOMAINS.join(' or ')} email addresses`;
    }
    return null;
  };

  const validatePassword = (pwd) => {
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return null;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!email || !password || !confirmPassword) {
      setLocalError('Please fill in all fields');
      return;
    }

    const domainError = validateEmailDomain(email);
    if (domainError) {
      setLocalError(domainError);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setLocalError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    setCreating(true);
    try {
      const result = await signUp(email, password);
      if (result?.user?.identities?.length === 0) {
        setLocalError('An account with this email already exists');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const displayError = localError || error;

  if (success) {
    return (
      <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-status-success-bg rounded-full flex items-center justify-center">
              <CheckCircle size={32} className="text-status-success" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-locate2-dark-blue mb-4">
            Check your email
          </h1>
          <p className="text-locate2-light-gray mb-6">
            We've sent a confirmation link to <strong className="text-locate2-dark-blue">{email}</strong>.
            Please check your email and click the link to activate your account.
          </p>
          <button
            onClick={onSwitchToLogin}
            className="w-full bg-locate2-primary text-white rounded-lg px-4 py-3 font-medium hover:bg-locate2-dark-blue transition-all"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 bg-locate2-primary rounded-lg flex items-center justify-center">
              <Truck size={24} className="text-white" />
            </div>
            <span className="text-2xl font-bold text-locate2-dark-blue">Locate2u</span>
          </div>
          <h1 className="text-2xl font-bold text-locate2-dark-blue mb-2">
            Create an Account
          </h1>
          <p className="text-locate2-light-gray">Sign up to get started</p>
        </div>

        {displayError && (
          <div className="mb-4 p-3 bg-status-error-bg text-status-error rounded-lg text-sm">
            {displayError}
          </div>
        )}

        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              Email
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Enter your email"
                disabled={creating}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              Password
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Create a password"
                disabled={creating}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-locate2-light-gray hover:text-locate2-medium-gray"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="mt-1 text-xs text-locate2-light-gray">
              Must be at least 6 characters
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Confirm your password"
                disabled={creating}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-locate2-light-gray hover:text-locate2-medium-gray"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 bg-locate2-primary text-white rounded-lg px-4 py-3 font-medium hover:bg-locate2-dark-blue transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            {creating ? <LoadingSpinner size="sm" className="border-white border-t-transparent" /> : null}
            {creating ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-locate2-light-gray text-sm">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-locate2-primary hover:text-locate2-dark-blue font-medium transition-colors"
            >
              Sign in
            </button>
          </p>
        </div>

        <div className="mt-4 text-center">
          <p className="text-locate2-light-gray text-xs">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
